/**
 * PVT — Zoom para imágenes de contenido RTE.
 *
 * Envuelve cada <img> del contenido enriquecido (artículos de blog y páginas) en un
 * botón que abre la imagen en un <dialog> nativo a pantalla completa.
 *
 * Se monta sobre el HTML que produce el editor de Shopify, por lo que no requiere
 * ningún cambio en el contenido de los posts. Es un componente propio (no de Horizon)
 * para que las actualizaciones del tema no lo pisen.
 */

const DIALOG_ID = 'pvt-rte-zoom-dialog';

/** Imágenes que no deben abrir zoom: iconos, logos y cualquier cosa muy chica. */
const MIN_NATURAL_WIDTH = 200;

class PvtRteZoom extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  connectedCallback() {
    // El contenido RTE puede venir dentro de <rte-formatter>; tomamos todas las imágenes
    // del subárbol, pero ignoramos las que ya estén dentro de un enlace del autor.
    this.#enhanceImages();
  }

  #enhanceImages() {
    const images = this.querySelectorAll('img:not([data-pvt-zoom-ready])');

    for (const image of images) {
      if (!(image instanceof HTMLImageElement)) continue;

      // Respetamos los enlaces que ya puso el autor en el editor.
      if (image.closest('a')) continue;

      // Permite excluir puntualmente una imagen desde el editor.
      if (image.hasAttribute('data-pvt-no-zoom')) continue;

      image.setAttribute('data-pvt-zoom-ready', '');

      // Si la imagen ya cargó podemos filtrar por tamaño de inmediato; si no,
      // esperamos al load para no descartar por un naturalWidth aún en 0.
      if (image.complete) {
        this.#wrapIfLargeEnough(image);
      } else {
        image.addEventListener('load', () => this.#wrapIfLargeEnough(image), { once: true });
      }
    }
  }

  /** @param {HTMLImageElement} image */
  #wrapIfLargeEnough(image) {
    if (image.naturalWidth && image.naturalWidth < MIN_NATURAL_WIDTH) return;
    this.#wrap(image);
  }

  /** @param {HTMLImageElement} image */
  #wrap(image) {
    const parent = image.parentNode;
    if (!parent || image.closest('.pvt-rte-zoom__trigger')) return;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'pvt-rte-zoom__trigger';
    trigger.setAttribute('aria-label', this.#zoomLabel(image));

    parent.insertBefore(trigger, image);
    trigger.appendChild(image);

    trigger.addEventListener('click', () => this.#open(image));
  }

  /** @param {HTMLImageElement} image */
  #zoomLabel(image) {
    const base = this.dataset.zoomLabel || 'Ampliar imagen';
    return image.alt ? `${base}: ${image.alt}` : base;
  }

  /**
   * El diálogo es único y compartido por todas las imágenes de la página:
   * se crea la primera vez que se necesita y luego solo se le cambia el contenido.
   */
  #getDialog() {
    if (this.#dialog?.isConnected) return this.#dialog;

    const existing = document.getElementById(DIALOG_ID);
    if (existing instanceof HTMLDialogElement) {
      this.#dialog = existing;
      return existing;
    }

    const dialog = document.createElement('dialog');
    dialog.id = DIALOG_ID;
    dialog.className = 'pvt-rte-zoom__dialog';

    const closeLabel = this.dataset.closeLabel || 'Cerrar';

    dialog.innerHTML = `
      <div class="pvt-rte-zoom__inner">
        <button type="button" class="pvt-rte-zoom__close" aria-label="${closeLabel}">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <img class="pvt-rte-zoom__image" alt="">
      </div>
    `;

    document.body.appendChild(dialog);

    dialog.querySelector('.pvt-rte-zoom__close')?.addEventListener('click', () => dialog.close());

    // Cerrar al hacer clic fuera de la imagen (en el backdrop o el área vacía).
    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.pvt-rte-zoom__image')) return;
      dialog.close();
    });

    // El scroll del fondo se bloquea mientras el modal está abierto.
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('pvt-rte-zoom-open');
    });

    this.#dialog = dialog;
    return dialog;
  }

  /** @param {HTMLImageElement} source */
  #open(source) {
    const dialog = this.#getDialog();
    const target = dialog.querySelector('.pvt-rte-zoom__image');
    if (!(target instanceof HTMLImageElement)) return;

    // Shopify sirve las imágenes con parámetros de tamaño; pedimos la versión grande
    // para que el zoom valga la pena, con fallback al src original.
    target.src = upscaleShopifyUrl(source.currentSrc || source.src);
    target.alt = source.alt || '';

    // En móvil las capturas apaisadas se rotan para aprovechar el alto del viewport;
    // el CSS decide cuándo aplicarlo, aquí solo se informa la orientación.
    const isLandscape = source.naturalWidth > source.naturalHeight;
    target.dataset.orientation = isLandscape ? 'landscape' : 'portrait';

    document.documentElement.classList.add('pvt-rte-zoom-open');
    dialog.showModal();

    // Si la imagen desborda (móvil + captura apaisada) el scroll arranca al
    // centro, no en el borde: si no, se abre mostrando un margen vacío.
    const inner = dialog.querySelector('.pvt-rte-zoom__inner');
    if (inner) {
      const centerScroll = () => {
        inner.scrollLeft = (inner.scrollWidth - inner.clientWidth) / 2;
        inner.scrollTop = (inner.scrollHeight - inner.clientHeight) / 2;
      };
      target.complete ? centerScroll() : target.addEventListener('load', centerScroll, { once: true });
    }
  }
}

/**
 * Sube la resolución de una URL del CDN de Shopify reemplazando el parámetro width.
 * Si no es una URL del CDN se devuelve tal cual.
 *
 * @param {string} url
 * @returns {string}
 */
function upscaleShopifyUrl(url) {
  if (!url || !url.includes('cdn.shopify.com')) return url;

  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.searchParams.has('width')) {
      parsed.searchParams.set('width', '2000');
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

if (!customElements.get('pvt-rte-zoom')) {
  customElements.define('pvt-rte-zoom', PvtRteZoom);
}
